import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Accessibility Announcer
 * Provides ARIA live region for screen reader announcements
 * Follows WCAG 2.1 guidelines for status messages
 */
const AccessibilityAnnouncer = () => {
  const [announcement, setAnnouncement] = useState('');
  const [politeness, setPoliteness] = useState('polite');

  useEffect(() => {
    // Listen for custom announcement events
    const handleAnnouncement = (event) => {
      const { message, priority = 'polite' } = event.detail;
      setPoliteness(priority); // 'polite' or 'assertive'
      setAnnouncement(message);
      
      // Clear announcement after it's been read
      setTimeout(() => setAnnouncement(''), 1000);
    };

    window.addEventListener('announce', handleAnnouncement);
    return () => window.removeEventListener('announce', handleAnnouncement);
  }, []);

  return (
    <>
      {/* Polite announcements (non-urgent) */}
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {politeness === 'polite' && announcement}
      </Box>

      {/* Assertive announcements (urgent) */}
      <Box
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {politeness === 'assertive' && announcement}
      </Box>
    </>
  );
};

/**
 * Helper function to announce messages to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - 'polite' (default) or 'assertive' (urgent)
 */
export const announce = (message, priority = 'polite') => {
  const event = new CustomEvent('announce', {
    detail: { message, priority },
  });
  window.dispatchEvent(event);
};

export default AccessibilityAnnouncer;
